import React from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../components/Modal';
import EpisodeMultiSelect from '../../../components/EpisodeMultiSelect';
import type { AddingNewElement } from './AddNewElementRow';

interface AddElementModalProps {
  open: boolean;
  title: string;
  descPlaceholder: string;
  value: AddingNewElement;
  onChange: React.Dispatch<React.SetStateAction<AddingNewElement | null>>;
  onConfirm: (name: string, desc: string, episodes: number[]) => void;
  onClose: () => void;
  availableEpisodes: readonly { episodeIndex: number; title?: string }[];
  themeColor?: string;
}

export default function AddElementModal({
  open, title, descPlaceholder, value, onChange, onConfirm, onClose, availableEpisodes, themeColor,
}: AddElementModalProps) {
  const { t } = useTranslation('projectDetail');
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      width="560px"
      dismissible={!value.loading}
      footer={
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={value.loading}>
            {t('cancel', { ns: 'common' })}
          </button>
          <button
            className="btn"
            onClick={() => onConfirm(value.name, value.desc, value.episodes)}
            disabled={value.loading || !value.name.trim()}
            style={{ background: themeColor || 'var(--primary)', color: '#fff', border: 'none' }}
          >
            {value.loading ? t('adding', { ns: 'common' }) : t('confirm', { ns: 'common' })}
          </button>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <input
          type="text"
          value={value.name}
          onChange={e => onChange(prev => (prev ? { ...prev, name: e.target.value } : prev))}
          placeholder={t('addElement.namePlaceholder')}
          autoFocus
          style={{ fontSize: '0.85rem', border: '1px solid var(--primary)', borderRadius: '4px', padding: '6px 10px' }}
        />
        <textarea
          value={value.desc}
          onChange={e => onChange(prev => (prev ? { ...prev, desc: e.target.value } : prev))}
          placeholder={descPlaceholder}
          rows={3}
          style={{ fontSize: '0.85rem', border: '1px solid var(--border)', borderRadius: '4px', padding: '6px 10px', resize: 'vertical', fontFamily: 'inherit' }}
        />
        <div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', marginBottom: 4 }}>
            {t('addElement.episodeHint')}
          </div>
          <EpisodeMultiSelect
            episodes={availableEpisodes}
            selected={value.episodes}
            onChange={next => onChange(prev => (prev ? { ...prev, episodes: next } : prev))}
            disabled={value.loading}
          />
        </div>
      </div>
    </Modal>
  );
}
